import { Box, HStack, Icon, Spinner, Text, VStack } from "@chakra-ui/react";
import { Step } from "../types";
import { FaCheck, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const stepsForFlow = (steps: Step[]) => {
  if (steps.at(0) === "create_campaign") {
    return ["create_campaign", "create_adset", "create_creative", "review"];
  }

  const step = steps.at(-1);
  if (["adset", "edit_adset", "list_ads", "edit_creative"].includes(step)) {
    return [step];
  }

  if (step === "edit_campaign") {
    return ["edit_campaign", "review"];
  }

  return [step];
};

const StepNext = ({ step }: { step: Step }) => {
  if (step === "review") {
    return (
      <>
        <Text>Publish</Text>
        <Icon fontSize="xs" as={FaCheck} />
      </>
    );
  }

  return (
    <>
      <Text>Next</Text>
      <Icon fontSize="xs" as={FaChevronRight} />
    </>
  );
};

const NextButton = ({
  isLoading,
  onNext,
  currentStep,
}: {
  isLoading: boolean;
  onNext: () => void;
  currentStep: Step;
}) => {
  return (
    <Box
      as="button"
      px={[3, 5]}
      py={2}
      minW={[24, 32]}
      borderRadius="md"
      disabled={isLoading}
      bg="gray.700"
      color="white"
      textAlign="center"
      fontSize="sm"
      fontWeight="semibold"
      onClick={onNext}
      _hover={{ bg: "gray.800" }}
      _active={{ bg: "gray.900" }}
    >
      <HStack spacing={2} justifyContent={"space-between"}>
        {isLoading ? (
          <Box w="full" display="flex" justifyContent="center">
            <Spinner size="sm" />
          </Box>
        ) : (
          <StepNext step={currentStep} />
        )}
      </HStack>
    </Box>
  );
};

interface ProgressBarProps {
  steps: Step[];
  currentStep: Step;
  isLoading?: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

const WizardNavigationBar = ({
  steps,
  currentStep,
  isLoading,
  onPrevious,
  onNext,
}: ProgressBarProps) => {
  const bubbles = stepsForFlow(steps);

  return (
    <Box
      w="full"
      backgroundColor="white"
      p={2}
      pt={1}
      borderRadius="xl"
      border="1px"
      borderColor="gray.200"
      boxShadow="md"
    >
      <VStack spacing={2} w="full">
        {/* Progress Bar */}
        <HStack w="full" spacing={1}>
          {bubbles.map((stepToRender, index) => (
            <Box
              key={`${stepToRender}-${index}`}
              flex={1}
              h={1}
              bg={stepToRender === currentStep ? "gray.700" : "gray.200"}
              borderRadius="full"
            />
          ))}
        </HStack>

        {/* Navigation and Step Info */}
        <HStack w="full" justify="space-between" align="center">
          <Box
            as="button"
            px={[3, 5]}
            py={2}
            minW={[24, 32]}
            disabled={isLoading}
            borderRadius="md"
            border="1px"
            borderColor="gray.300"
            textAlign="center"
            bg="white"
            color="gray.700"
            fontSize="sm"
            fontWeight="semibold"
            onClick={onPrevious}
            _hover={{ bg: "gray.50" }}
            _active={{ bg: "gray.100" }}
          >
            <HStack spacing={2} justifyContent={"space-between"}>
              {isLoading ? (
                <Box w="full" display="flex" justifyContent="center">
                  <Spinner size="sm" />
                </Box>
              ) : (
                <>
                  <Icon fontSize="xs" as={FaChevronLeft} />
                  <Text>Previous</Text>
                </>
              )}
            </HStack>
          </Box>

          <VStack spacing={0} align="center">
            <Text fontSize="sm" fontWeight="medium" color="gray.700">
              Step {bubbles.indexOf(currentStep) + 1}/{bubbles.length}
            </Text>
            <Text fontSize="xs" color="gray.500">
              Draft saved
            </Text>
          </VStack>
          <NextButton
            isLoading={isLoading}
            onNext={onNext}
            currentStep={currentStep}
          />
        </HStack>
      </VStack>
    </Box>
  );
};

export default WizardNavigationBar;
